import React, { useEffect, useMemo, useRef, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ServicePageHeader } from "@/components/ServiceHistoryButton";
import { SimammaBadge } from "@/components/Logo";
import { toast } from "sonner";
import { ConfirmButton } from "@/components/ConfirmButton";
import { AdminCost } from "@/components/AdminSourceCost";
import { EsimComparison } from "@/components/EsimComparison";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { PurchaseReceipt } from "@/components/PurchaseReceipt";
import { Signal, Check, QrCode, Search, UserPlus, RefreshCw, AlertTriangle, Info, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const dealerPrices = (p) => `cliente ${euro(p.customer_price ?? p.sale_price)} · tu ${euro(p.sale_price)}`;
const DealerPriceLine = ({ p, testid }) => <span data-testid={testid}>Prezzo al cliente <span className="text-foreground font-semibold">{euro(p.customer_price ?? p.sale_price)}</span> · Paghi tu <span className="text-foreground font-semibold">{euro(p.sale_price)}</span> · Guadagni <span className="text-emerald-500 font-semibold">{euro(p.dealer_margin ?? 0)}</span></span>;

// Aiuto ricerca: molti scrivono i Paesi in italiano, ma il catalogo Airalo è in inglese.
const IT_TO_EN = {
  italia: "italy", francia: "france", spagna: "spain", germania: "germany", "stati uniti": "united states",
  "regno unito": "united kingdom", inghilterra: "united kingdom", svizzera: "switzerland", grecia: "greece",
  portogallo: "portugal", "paesi bassi": "netherlands", olanda: "netherlands", belgio: "belgium", irlanda: "ireland",
  svezia: "sweden", danimarca: "denmark", norvegia: "norway", finlandia: "finland", polonia: "poland",
  ungheria: "hungary", romania: "romania", croazia: "croatia", turchia: "turkey", egitto: "egypt", marocco: "morocco",
  tunisia: "tunisia", "emirati arabi": "united arab emirates", "arabia saudita": "saudi arabia", giappone: "japan",
  cina: "china", "corea del sud": "south korea", thailandia: "thailand", vietnam: "vietnam", indonesia: "indonesia",
  india: "india", singapore: "singapore", malesia: "malaysia", filippine: "philippines", australia: "australia",
  "nuova zelanda": "new zealand", canada: "canada", messico: "mexico", brasile: "brazil", argentina: "argentina",
  cile: "chile", russia: "russia", ucraina: "ukraine", israele: "israel", "sud africa": "south africa",
  kenya: "kenya", nigeria: "nigeria", austria: "austria",
};
const searchCandidates = (q) => {
  const out = [q];
  for (const [it, en] of Object.entries(IT_TO_EN)) {
    if (it.includes(q) || q.includes(it)) out.push(en);
  }
  return out;
};
const isUnlimited = (p) => /unlim|illim/i.test(`${p.data || ""}`);
const gbValue = (p) => {
  const m = `${p.data || ""}`.match(/([\d.]+)\s*gb/i);
  return m ? parseFloat(m[1]) : 9999;
};

// Bandierine per la vista "Per Paese"
const EN_ISO = { italy: "IT", france: "FR", spain: "ES", germany: "DE", "united states": "US", "united kingdom": "GB", switzerland: "CH", greece: "GR", portugal: "PT", netherlands: "NL", belgium: "BE", ireland: "IE", sweden: "SE", denmark: "DK", norway: "NO", finland: "FI", poland: "PL", hungary: "HU", romania: "RO", croatia: "HR", turkey: "TR", egypt: "EG", morocco: "MA", tunisia: "TN", "united arab emirates": "AE", "saudi arabia": "SA", japan: "JP", china: "CN", "south korea": "KR", thailand: "TH", vietnam: "VN", indonesia: "ID", india: "IN", singapore: "SG", malaysia: "MY", philippines: "PH", australia: "AU", "new zealand": "NZ", canada: "CA", mexico: "MX", brazil: "BR", argentina: "AR", chile: "CL", russia: "RU", ukraine: "UA", israel: "IL", "south africa": "ZA", kenya: "KE", nigeria: "NG", austria: "AT" };
const flagEmoji = (name) => {
  const iso = EN_ISO[(name || "").toLowerCase()];
  if (!iso) return "🌍";
  return String.fromCodePoint(...[...iso].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};
// Pacchetti "SIM Mondo": globali/regionali multi-Paese
const MONDO_RE = /global|world|mondo|discover|region|europe|multi|100\+|130\+/i;

export default function SimammaEsim() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const checkoutBusy = useRef(false);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [meta, setMeta] = useState({ source: "mock", mode: "sandbox" });
  const [mode, setMode] = useState("new"); // new | existing
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [form, setForm] = useState({ customer_id: "", name: "", email: "", phone: "", package_id: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [acceptNoRefund, setAcceptNoRefund] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    let active = true;
    setCatalogLoading(true); setCatalogError(""); setPackages([]);
    if (!isCustomer) api.get("/customers").then((r) => { if (active) setCustomers(r.data); }).catch(() => {});
    api.get("/esim/packages").then((r) => {
      if (active) { setPackages(r.data.packages || []); setMeta({ source: r.data.source, mode: r.data.mode }); }
    }).catch((err) => { if (active) setCatalogError(formatApiErrorDetail(err.response?.data?.detail) || "Catalogo non disponibile"); })
      .finally(() => { if (active) setCatalogLoading(false); });
    return () => { active = false; };
  }, [isCustomer, catalogAttempt]);

  const selectedPkg = packages.find((p) => p.package_id === form.package_id);
  const countries = useMemo(() => Array.from(new Set(packages.filter((p) => p.package_type !== "global").map((p) => p.country).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [packages]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cands = q ? searchCandidates(q) : [];
    let list = packages.filter((p) => {
      if (p.package_type === "global") return false;
      if (countryFilter !== "all" && p.country !== countryFilter) return false;
      if (q) {
        const hay = `${p.title || ""} ${p.country || ""}`.toLowerCase();
        if (!cands.some((c) => hay.includes(c))) return false;
      }
      return true;
    });
    // Per un singolo Paese (es. Italia) mostra i pacchetti dati con gli ILLIMITATI in cima.
    if (countryFilter !== "all") {
      list = [...list].sort((a, b) => {
        if (isUnlimited(a) !== isUnlimited(b)) return isUnlimited(a) ? -1 : 1;
        if (isUnlimited(a)) return (a.sale_price || 0) - (b.sale_price || 0);
        return gbValue(a) - gbValue(b);
      });
    }
    return list;
  }, [packages, search, countryFilter]);

  const applyItalia = () => {
    const it = countries.find((c) => c.toLowerCase() === "italy") || "Italy";
    setCountryFilter(it);
    setSearch("");
    setForm((f) => ({ ...f, package_id: "" }));
  };

  const pickFav = (en) => {
    const match = countries.find((c) => c.toLowerCase() === en.toLowerCase()) || countries.find((c) => c.toLowerCase().includes(en.toLowerCase()));
    if (match) { setCountryFilter(match); setSearch(""); }
    else { setCountryFilter("all"); setSearch(en); }
    setForm((f) => ({ ...f, package_id: "" }));
  };
  const FAVORITES = [["Italia", "Italy"], ["USA", "United States"], ["UK", "United Kingdom"], ["Spagna", "Spain"], ["Francia", "France"], ["Germania", "Germany"]];

  const [esimScope, setEsimScope] = useState("paese"); // mondo | paese
  const mondoPackages = useMemo(() => packages.filter((p) => p.package_type === "global" || MONDO_RE.test(`${p.country || ""} ${p.title || ""}`)).sort((a, b) => (a.sale_price || 0) - (b.sale_price || 0)), [packages]);

  // Geolocalizzazione best-effort: rileva il Paese e lo preseleziona se presente nel catalogo.
  const [geoCountry, setGeoCountry] = useState("");
  const geoTried = useRef(false);
  useEffect(() => {
    if (geoTried.current || countries.length === 0 || !navigator.geolocation) return;
    geoTried.current = true;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.get("/geo/reverse-country", { params: { lat: pos.coords.latitude, lon: pos.coords.longitude } });
        const name = (data.country || "").toLowerCase();
        if (!name) return;
        const match = countries.find((c) => c.toLowerCase() === name) || countries.find((c) => c.toLowerCase().includes(name));
        if (match) { setGeoCountry(match); setCountryFilter((cur) => (cur === "all" ? match : cur)); }
      } catch { /* best-effort, silenzioso */ }
    }, () => {}, { timeout: 8000, maximumAge: 600000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  const [tab, setTab] = useState("attiva");
  const [ricaricaIccid, setRicaricaIccid] = useState("");
  const [infoQ, setInfoQ] = useState("");
  const [infoRes, setInfoRes] = useState(null);
  const [infoBusy, setInfoBusy] = useState(false);
  const [usageMap, setUsageMap] = useState({});
  const [usageBusy, setUsageBusy] = useState("");
  const loadUsage = async (iccid, silent = false) => {
    if (!silent) setUsageBusy(iccid);
    try { const { data } = await api.get(`/esim/usage/${encodeURIComponent(iccid)}`); setUsageMap((m) => ({ ...m, [iccid]: data })); }
    catch (err) { if (!silent) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore utilizzo"); }
    finally { if (!silent) setUsageBusy(""); }
  };

  // Auto-carica il traffico residuo per ogni eSIM in lista e aggiorna ogni 60s
  useEffect(() => {
    if (!infoRes || infoRes.length === 0) return;
    const iccids = infoRes.map((r) => r.iccid).filter(Boolean);
    if (iccids.length === 0) return;
    const run = () => iccids.forEach((ic) => loadUsage(ic, true));
    run();
    const id = setInterval(run, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoRes]);

  const doLookup = async () => {
    setInfoBusy(true);
    try { const { data } = await api.get(`/esim/lookup?q=${encodeURIComponent(infoQ.trim())}`); setInfoRes(data.results || []); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore ricerca"); }
    finally { setInfoBusy(false); }
  };

  const doTopup = async () => {
    if (!ricaricaIccid.trim()) return toast.error("Inserisci l'ICCID della eSIM");
    if (!form.package_id) return toast.error("Seleziona un pacchetto");
    setLoading(true);
    try {
      const { data } = await api.post("/esim/topup", { iccid: ricaricaIccid.trim(), package_id: form.package_id });
      setResult(data);
      toast.success(`Ricarica completata — addebitati ${euro(data.amount)}`);
      setRicaricaIccid(""); setForm((f) => ({ ...f, package_id: "" }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore ricarica"); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.package_id) return toast.error("Seleziona un pacchetto eSIM");
    if (isCustomer) {
      if (!acceptNoRefund || checkoutBusy.current) return;
      checkoutBusy.current = true; setLoading(true);
      try {
        const { data } = await api.post("/esim/customer-checkout", { package_id: form.package_id, accept_no_refund: true });
        window.location.assign(data.checkout_url);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Pagamento non disponibile");
        checkoutBusy.current = false; setLoading(false);
      }
      return;
    }
    if (mode === "existing" && !form.customer_id) return toast.error("Seleziona un cliente");
    if (mode === "new" && !form.name.trim()) return toast.error("Inserisci almeno il nome del cliente");
    setLoading(true);
    try {
      const payload = mode === "existing"
        ? { customer_id: form.customer_id, package_id: form.package_id }
        : { name: form.name.trim(), email: form.email.trim() || null, phone: form.phone.trim() || null, package_id: form.package_id };
      const { data } = await api.post("/esim/sell-quick", payload);
      setResult(data);
      toast.success(`eSIM attivata${data.customer_created ? " e cliente creato" : ""} — addebitati ${euro(data.amount)}`);
      setForm({ customer_id: "", name: "", email: "", phone: "", package_id: "" }); setStep(0); setAcceptNoRefund(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore attivazione eSIM"); }
    finally { setLoading(false); }
  };

  return (
    <div className="service-page-wide space-y-5 w-full max-w-none" data-testid="simamma-esim-page">
      <ServicePageHeader service="esim">
        <div className="flex items-center gap-2"><SimammaBadge /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">VendEsim International</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2">SiMamma eSIM</h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="simamma-description">{isCustomer ? "Scegli destinazione e pacchetto, paga con carta e ritrova il QR di installazione in I miei acquisti. Verifica che il tuo dispositivo supporti le eSIM." : "eSIM internazionale Airalo. Scegli SIM Mondo oppure Per Paese cercando la destinazione. Il QR viene inviato via email."}</p>
        {isCustomer && <Link to="/app/miei-acquisti?tab=esim" className="inline-block mt-3 text-sm text-primary underline" data-testid="simamma-my-esims">Le mie eSIM</Link>}
      </ServicePageHeader>

      {catalogLoading && <p role="status" data-testid="simamma-catalog-loading">Caricamento catalogo…</p>}
      {catalogError && <div role="alert" data-testid="simamma-catalog-error"><p>{catalogError}</p><Button variant="outline" onClick={() => setCatalogAttempt((n) => n + 1)} data-testid="simamma-catalog-retry">Riprova</Button></div>}
      {!catalogLoading && !catalogError && packages.length === 0 && <p data-testid="simamma-catalog-empty">Nessun pacchetto acquistabile al momento.</p>}
      {!isCustomer && <div className="flex gap-2" data-testid="simamma-tabs">
        <Button size="sm" variant={tab === "attiva" ? "default" : "outline"} onClick={() => setTab("attiva")} data-testid="simamma-tab-attiva">Attiva SIM</Button>
        <Button size="sm" variant={tab === "ricarica" ? "default" : "outline"} onClick={() => setTab("ricarica")} data-testid="simamma-tab-ricarica">Ricarica</Button>
        <Button size="sm" variant={tab === "info" ? "default" : "outline"} onClick={() => setTab("info")} data-testid="simamma-tab-info">Info SIM</Button>
      </div>}

      {result && (
        <Card className="p-6 glow-border" data-testid="simamma-esim-result" style={{ background: "#0f1b2d" }}>
          <div className="flex items-center gap-2 text-teal-400"><Check className="w-5 h-5" /><span className="font-semibold">eSIM {result.package_title} pronta</span></div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 items-center">
            <img src={`${BACKEND}${result.qr_url}`} alt="QR eSIM" width={180} height={180} className="rounded-lg bg-white p-2" data-testid="simamma-esim-qr" />
            <div className="text-sm text-white/80 space-y-1 break-all">
              <p><span className="text-white/50">ICCID:</span> <span className="font-mono">{result.iccid}</span></p>
              <p><span className="text-white/50">LPA:</span> <span className="font-mono">{result.lpa}</span></p>
              <p><span className="text-white/50">Email cliente:</span> {result.email_sent ? "inviata ✅" : "non inviata"}</p>
              {result.mock && <p className="text-amber-400 text-xs">Modalità demo: eSIM simulata (non attivabile).</p>}
              {result.iccid && <div className="pt-2" data-testid="simamma-receipt-actions"><PurchaseReceipt refId={result.iccid} /></div>}
            </div>
          </div>
        </Card>
      )}

      {tab === "attiva" && (
      <Card className="p-6 glow-border">
        <form onSubmit={submit} className="space-y-5">
          {!isCustomer && <div className="flex flex-wrap items-center gap-1 pb-3 border-b border-border" data-testid="simamma-steps">{["Cliente", "Pacchetto", "Conferma"].map((l, i) => <React.Fragment key={l}>{i > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}<button type="button" disabled={i > step} onClick={() => setStep(i)} data-testid={`simamma-step-${i + 1}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${i === step ? "bg-primary text-primary-foreground font-semibold" : i < step ? "hover:bg-secondary" : "text-muted-foreground"}`}>{i < step ? <Check className="w-3.5 h-3.5" /> : <span className="font-bold">{i + 1}</span>}{l}</button></React.Fragment>)}</div>}
          {!isCustomer && step === 0 && <>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")} data-testid="simamma-mode-new"><UserPlus className="w-4 h-4 mr-1" /> Nuovo cliente</Button>
            <Button type="button" size="sm" variant={mode === "existing" ? "default" : "outline"} onClick={() => setMode("existing")} data-testid="simamma-mode-existing">Cliente registrato</Button>
          </div>

          {mode === "existing" ? (
            <div>
              <Label>Cliente</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="simamma-customer-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone && <> — <PhoneDisplay value={c.phone} data-testid={`simamma-customer-phone-${c.id}`} /></>}</SelectItem>)}</SelectContent>
              </Select>
              {customers.length === 0 && <p className="text-xs text-amber-500 mt-1">Nessun cliente: usa "Nuovo cliente".</p>}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="simamma-new-customer">
              <div><Label>Nome e cognome *</Label><Input className="mt-1.5" data-testid="simamma-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mario Rossi" /></div>
              <div><Label>Telefono</Label><Input type="tel" className="mt-1.5" data-testid="simamma-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39…" /></div>
              <div className="sm:col-span-2"><Label>Email (per invio QR)</Label><Input type="email" className="mt-1.5" data-testid="simamma-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.it" /></div>
            </div>
          )}
          </>}

          {(isCustomer || step === 1) && <div className="space-y-3">
            <Label>Pacchetto eSIM</Label>
            <div className="flex gap-2" data-testid="simamma-scope">
              <Button type="button" size="sm" variant={esimScope === "mondo" ? "default" : "outline"} onClick={() => { setEsimScope("mondo"); setForm((f) => ({ ...f, package_id: "" })); }} data-testid="simamma-scope-mondo">🌍 SIM Mondo · Global</Button>
              <Button type="button" size="sm" variant={esimScope === "paese" ? "default" : "outline"} onClick={() => { setEsimScope("paese"); setCountryFilter("all"); setSearch(""); setForm((f) => ({ ...f, package_id: "" })); }} data-testid="simamma-scope-paese">Per Paese</Button>
            </div>

            {esimScope === "mondo" ? (
              <div className="rounded-xl border-2 border-primary/50 bg-primary/5 p-5 flex items-start gap-4" data-testid="simamma-mondo-box">
                <div className="w-16 h-16 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center text-3xl shrink-0">🌐</div>
                <div className="flex-1 min-w-0">
                  <p className="font-head font-bold text-lg">SIM Mondo · Global</p>
                  <p className="text-xs text-muted-foreground mb-2">Un'unica eSIM Global che funziona in oltre 100 Paesi. Ideale per chi viaggia in più destinazioni.</p>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger data-testid="simamma-mondo-package"><SelectValue placeholder={`Scegli il pacchetto Mondo (${mondoPackages.length})`} /></SelectTrigger>
                    <SelectContent className="max-h-72">{mondoPackages.slice(0, 300).map((p) => <SelectItem data-testid={`simamma-global-option-${p.package_id}`} key={p.package_id} value={p.package_id}>{p.title} — {isCustomer ? euro(p.sale_price) : dealerPrices(p)} <AdminCost cost={p.net_price} testId={`simamma-global-cost-${p.package_id}`} /></SelectItem>)}</SelectContent>
                  </Select>
                  {mondoPackages.length === 0 && <p className="text-xs text-amber-500 mt-1">Nessun pacchetto globale nel catalogo corrente.</p>}
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2" data-testid="simamma-favorites">
                  {geoCountry && !FAVORITES.some(([, en]) => en.toLowerCase() === geoCountry.toLowerCase()) && (
                    <Button type="button" size="sm" variant={countryFilter.toLowerCase() === geoCountry.toLowerCase() ? "default" : "outline"} onClick={() => { setCountryFilter(geoCountry); setSearch(""); setForm((f) => ({ ...f, package_id: "" })); }} data-testid="simamma-fav-geo" title="Rilevato dalla tua posizione">📍 {flagEmoji(geoCountry)} {geoCountry}</Button>
                  )}
                  {FAVORITES.map(([lbl, en]) => (
                    <Button key={en} type="button" size="sm" variant={countryFilter.toLowerCase() === en.toLowerCase() ? "default" : "outline"} onClick={() => pickFav(en)} data-testid={`simamma-fav-${en.replace(/\s+/g, "-").toLowerCase()}`}>{flagEmoji(en)} {lbl}</Button>
                  ))}
                </div>
                <Select value={countryFilter} onValueChange={(v) => { setCountryFilter(v); setForm((f) => ({ ...f, package_id: "" })); }}>
                  <SelectTrigger data-testid="simamma-country"><SelectValue placeholder="Scegli un'altra destinazione…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {countries.map((c) => <SelectItem data-testid={`simamma-country-option-${c}`} key={c} value={c}>{flagEmoji(c)} {c}</SelectItem>)}
                  </SelectContent>
                </Select>
                {countryFilter !== "all" ? (
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger data-testid="simamma-package"><SelectValue placeholder={`Seleziona pacchetto (${filtered.length} disponibili)`} /></SelectTrigger>
                    <SelectContent className="max-h-72">{filtered.slice(0, 300).map((p) => <SelectItem data-testid={`simamma-package-option-${p.package_id}`} key={p.package_id} value={p.package_id}>{flagEmoji(p.country)} {p.title} — {isCustomer ? euro(p.sale_price) : dealerPrices(p)} <AdminCost cost={p.net_price} testId={`simamma-package-cost-${p.package_id}`} /></SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="simamma-pick-hint">Scegli prima una destinazione (es. Italia) o selezionala dal menu a tendina per vedere i pacchetti disponibili.</p>
                )}
              </>
            )}
            {selectedPkg && <p data-testid="simamma-selected-package" className="text-xs text-muted-foreground mt-1">{flagEmoji(selectedPkg.country)} {selectedPkg.country} • {selectedPkg.data} • {selectedPkg.validity} • {isCustomer ? <>Prezzo: <span className="text-foreground font-semibold">{euro(selectedPkg.sale_price)}</span></> : <DealerPriceLine p={selectedPkg} testid="simamma-selected-prices" />}</p>}
            {isCustomer && <EsimComparison packages={packages} selected={selectedPkg} onChoose={(pkg) => {
              const global = pkg.package_type === "global" || MONDO_RE.test(`${pkg.country || ""} ${pkg.title || ""}`);
              setEsimScope(global ? "mondo" : "paese"); setCountryFilter(global ? "all" : pkg.country); setSearch("");
              setForm((f) => ({ ...f, package_id: pkg.package_id })); setAcceptNoRefund(false);
            }} />}
          </div>}

          {!isCustomer && step === 2 && <div className="rounded-xl border border-border p-4 text-sm space-y-1" data-testid="simamma-recap">
            <p><span className="text-muted-foreground">Cliente:</span> <strong>{mode === "existing" ? (customers.find((c) => c.id === form.customer_id)?.name || "—") : form.name}</strong>{mode === "new" && form.email ? ` · ${form.email}` : ""}</p>
            {selectedPkg && <p><span className="text-muted-foreground">Pacchetto:</span> {flagEmoji(selectedPkg.country)} {selectedPkg.title} · {selectedPkg.data} · {selectedPkg.validity}</p>}
            {selectedPkg && <p className="text-muted-foreground"><DealerPriceLine p={selectedPkg} testid="simamma-recap-prices" /></p>}
          </div>}

          {!isCustomer && step < 2 && <div className="flex items-center gap-2">
            {step > 0 && <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} data-testid="simamma-back">Indietro</Button>}
            <Button type="button" disabled={step === 0 ? (mode === "existing" ? !form.customer_id : !form.name.trim()) : !form.package_id} onClick={() => setStep(step + 1)} className="ml-auto gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="simamma-next">Avanti <ChevronRight className="w-4 h-4" /></Button>
          </div>}

          {(isCustomer || step === 2) && <>
          <div className="w-full basis-full rounded-xl border border-border bg-secondary/40 p-4 space-y-3" data-testid="simamma-norefund-notice">
            <div className="flex items-start gap-2.5">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              {isCustomer ? <><strong>eSIM internazionale non rimborsabile dopo l'emissione.</strong> Verifica destinazione, dati, validità e compatibilità del dispositivo prima di confermare.</> : <><strong>eSIM internazionale non rimborsabile.</strong> Una volta creato il QR code la eSIM è già attiva: la vendita è definitiva e non è possibile rimborsarla o annullarla. Verifica paese, dati e validità prima di confermare.</>}
            </p>
            </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer border-t border-border pt-3" data-testid="simamma-norefund-check-label">
            <input type="checkbox" className="mt-1" checked={acceptNoRefund} onChange={(e) => setAcceptNoRefund(e.target.checked)} data-testid="simamma-norefund-check" />
            <span>{isCustomer ? "Ho verificato la compatibilità del dispositivo e accetto l'emissione immediata della eSIM dopo il pagamento, non rimborsabile dopo la generazione del QR." : <>Confermo di aver informato il cliente e accetto che, una volta creato il QR code, la eSIM è attiva e <strong>non rimborsabile</strong>.</>}</span>
          </label>
          </div>

          {isCustomer ? <Button type="submit" disabled={loading || !acceptNoRefund || !selectedPkg || catalogLoading || !!catalogError} className="gap-2 w-full" data-testid="simamma-customer-checkout">
            {loading ? "Apertura pagamento…" : `Paga con carta${selectedPkg ? ` — ${euro(selectedPkg.sale_price)}` : ""}`}
          </Button> : <ConfirmButton onConfirm={() => submit()} disabled={loading || !acceptNoRefund} className="gap-2 w-full" data-testid="simamma-submit"
            title="Confermi l'attivazione della eSIM?"
            description={(selectedPkg ? `Verrà attivata "${selectedPkg.title}" e addebitati ${euro(selectedPkg.sale_price)} dal tuo Plafond servizi. ` : "Verrà addebitato il costo del pacchetto selezionato. ") + "⚠️ Attenzione: una volta creato il QR la eSIM è già attiva e NON è rimborsabile."}
            confirmText="Attiva eSIM">
            <QrCode className="w-4 h-4" /> {loading ? "Attivazione eSIM…" : `Crea cliente e attiva eSIM${selectedPkg ? ` — ${euro(selectedPkg.sale_price)}` : ""}`}
          </ConfirmButton>}
          {!isCustomer && <Button type="button" variant="ghost" onClick={() => setStep(1)} data-testid="simamma-back-confirm">Indietro</Button>}
          </>}
        </form>
      </Card>
      )}

      {tab === "ricarica" && (
        <Card className="p-6 glow-border space-y-4" data-testid="simamma-ricarica">
          <p className="text-sm text-muted-foreground">Ricarica una eSIM in scadenza acquistando un nuovo pacchetto dati per lo stesso cliente (nuovo QR). Il top-up nativo Airalo sarà collegato in seguito.</p>
          <div>
            <Label>ICCID della eSIM</Label>
            <Input className="mt-1.5" value={ricaricaIccid} onChange={(e) => setRicaricaIccid(e.target.value)} placeholder="Es. 8939..." data-testid="simamma-ricarica-iccid" />
            <p className="text-xs text-muted-foreground mt-1">Non conosci l'ICCID? Trovalo in "Info SIM" cercando per nome cliente.</p>
          </div>
          <div>
            <Label>Nuovo pacchetto</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              <Button type="button" size="sm" variant={String(countryFilter).toLowerCase() === "italy" ? "default" : "outline"} onClick={applyItalia}>SIM Italia (dati)</Button>
              <Button type="button" size="sm" variant={countryFilter === "all" && !search ? "default" : "outline"} onClick={() => { setCountryFilter("all"); setSearch(""); }}>Tutte</Button>
            </div>
            <div className="relative mt-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca paese o pacchetto…" data-testid="simamma-ricarica-search" />
            </div>
            <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
              <SelectTrigger className="mt-2" data-testid="simamma-ricarica-package"><SelectValue placeholder={`Seleziona pacchetto (${filtered.length})`} /></SelectTrigger>
              <SelectContent className="max-h-72">{filtered.slice(0, 300).map((p) => <SelectItem key={p.package_id} data-testid={`simamma-ricarica-option-${p.package_id}`} value={p.package_id}>{p.title} — {dealerPrices(p)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ConfirmButton disabled={loading} className="w-full gap-2" onConfirm={() => doTopup()} data-testid="simamma-ricarica-submit" title="Confermi la ricarica eSIM?" description={selectedPkg ? `Verrà acquistato "${selectedPkg.title}" e addebitati ${euro(selectedPkg.sale_price)} dal tuo Plafond servizi (prezzo al cliente ${euro(selectedPkg.customer_price ?? selectedPkg.sale_price)}).` : "Verrà addebitato il costo del pacchetto."} confirmText="Ricarica"><QrCode className="w-4 h-4" /> {loading ? "Ricarica…" : `Ricarica${selectedPkg ? ` — ${euro(selectedPkg.sale_price)}` : ""}`}</ConfirmButton>
        </Card>
      )}

      {tab === "info" && (
        <Card className="p-6 glow-border space-y-4" data-testid="simamma-info">
          <p className="text-sm text-muted-foreground">Cerca una eSIM per ICCID o nome cliente per vedere pacchetto, data di attivazione, QR e il traffico residuo (aggiornato automaticamente ogni 60s).</p>
          <div className="flex gap-2">
            <Input value={infoQ} onChange={(e) => setInfoQ(e.target.value)} placeholder="ICCID o nome cliente" data-testid="simamma-info-query" onKeyDown={(e) => e.key === "Enter" && doLookup()} />
            <Button onClick={doLookup} disabled={infoBusy} data-testid="simamma-info-search"><Search className="w-4 h-4 mr-1" />{infoBusy ? "…" : "Cerca"}</Button>
          </div>
          {infoRes && (
            <div className="space-y-2" data-testid="simamma-info-results">
              {infoRes.length === 0 && <p className="text-sm text-muted-foreground">Nessuna eSIM trovata.</p>}
              {infoRes.map((r, i) => (
                <div key={i} className="rounded-lg border border-border p-3 text-sm space-y-2" data-testid={`simamma-info-item-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate"><EntityLink kind="customer" id={r.customer_id} data-testid={`esim-customer-link-${i}`}>{r.customer_name || "—"}</EntityLink> · <EntityLink kind="esim" id={r.id} className="font-mono text-xs" data-testid={`esim-record-link-${i}`}>{r.iccid}</EntityLink></p>
                      <p className="text-xs text-muted-foreground truncate">{r.package_title} · attivata {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}{r.mock ? " · demo" : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button type="button" size="sm" variant="ghost" disabled={usageBusy === r.iccid} onClick={() => loadUsage(r.iccid)} title="Aggiorna traffico" data-testid={`simamma-usage-${i}`}><RefreshCw className={`w-4 h-4 ${usageBusy === r.iccid ? "animate-spin" : ""}`} /></Button>
                      {r.qr_token && <a href={`${BACKEND}/api/esim/qr/${r.qr_token}`} target="_blank" rel="noreferrer" className="text-primary text-xs underline">QR</a>}
                    </div>
                  </div>
                  <div className="text-xs rounded bg-secondary/40 px-2 py-1.5" data-testid={`simamma-usage-res-${i}`}>
                    {usageMap[r.iccid]
                      ? (usageMap[r.iccid].available
                          ? <span>{usageMap[r.iccid].is_unlimited ? "Dati illimitati" : `Rimasti ${usageMap[r.iccid].remaining_gb ?? "?"} GB / ${usageMap[r.iccid].total_gb ?? "?"} GB`}{usageMap[r.iccid].expires_at ? ` · scade ${new Date(usageMap[r.iccid].expires_at).toLocaleDateString()}` : ""}{usageMap[r.iccid].status ? ` · ${usageMap[r.iccid].status}` : ""}</span>
                          : <span className="text-muted-foreground">{usageMap[r.iccid].reason || "Utilizzo non disponibile"}</span>)
                      : <span className="text-muted-foreground">Traffico: caricamento…</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
